import type { CSSProperties } from 'react';

/**
 * Loading placeholder — reference §8. Skeletons match the shape of the content they
 * replace so nothing shifts when data lands. A centred spinner is never used for content;
 * spinners are only for action triggers.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block animate-pulse rounded bg-slate-200 ${className}`}
    />
  );
}

/**
 * A run of skeleton table rows, sized to the real column count.
 *
 * Marked aria-hidden and data-skeleton: a screen reader announcing eight empty rows is
 * worse than silence, and the attribute lets tests distinguish placeholders from real
 * data instead of counting `tbody tr` and getting the wrong answer.
 *
 * **The bar is sized to ITS OWN CELL, never to a fixed width.** It was `w-8` on the first
 * column and `w-24` on every other, which assumed `<IndexTable>`'s own leading gutters —
 * correct there and wrong for any caller that passes DATA columns: the anchor got a 32px
 * stub in a 365px column while a 96px bar spilled out of every narrower one, including
 * `<IndexTable>`'s own 44px `#` gutter. A proportion capped at 96px fits whatever the
 * colgroup gives the cell.
 */
export function SkeletonRows({
  rows = 8,
  columns,
  height,
  cell,
}: {
  rows?: number;
  columns: number;
  /**
   * The table's dragged row height, so the placeholder rows are the size the real ones will
   * be. Without it a table with taller rows visibly resettles the moment data lands.
   */
  height?: number;
  /**
   * The row chrome the caller's own grid draws — column rules, frozen offsets, alignment.
   *
   * A skeleton that omits them makes the grid APPEAR the moment data lands, which is the
   * resettle this component exists to prevent: `<StockLevelsIndexTable>` drew its vertical
   * rules and froze two columns on the real rows and on nothing while loading, so the table
   * had no columns until it had data.
   */
  cell?: (index: number) => { className?: string; style?: CSSProperties };
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr
          key={rowIndex}
          style={height !== undefined ? { height } : undefined}
          /* slate-200, with the row rules the three grids moved to on 21 Sep 2026 — a
             skeleton left at slate-100 is a line that darkens when the data arrives. */
          className="border-t border-slate-200"
          aria-hidden="true"
          data-skeleton="true"
        >
          {Array.from({ length: columns }, (_, columnIndex) => {
            const chrome = cell?.(columnIndex);

            return (
              <td
                key={columnIndex}
                className={`px-3 py-2 ${chrome?.className ?? ''}`}
                style={chrome?.style}
              >
                {/* An ARBITRARY max rather than `max-w-24`: nothing else in the product uses
                    the numeric scale for it, and a class Tailwind does not emit fails by
                    leaving the bar unbounded, which is silent. */}
                <Skeleton className="h-3.5 w-2/3 max-w-[96px]" />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/**
 * A run of skeleton FORM FIELDS, for a modal still fetching what its controls will show.
 *
 * **It mirrors the real field's box exactly** — `flex flex-col gap-1.5`, a label row at the
 * `min-h-5` every shared input reserves, then a `min-h-10` control. A skeleton a few pixels
 * off its own content is the resettle this exists to prevent, and a form is the worst place
 * for one: the reader is already reaching for the first field.
 *
 * **Two columns by default, because `sm:grid-cols-2` is the form grid** (the one exception
 * being the person's name row). A caller whose form is a single stack passes `columns={1}`
 * rather than letting the placeholder promise a shape the form does not have.
 *
 * `aria-hidden` and `data-skeleton` for `SkeletonRows`' reasons: a screen reader announcing
 * six empty fields is worse than silence, and a test can tell a placeholder from a control.
 */
export function SkeletonFields({
  rows = 4,
  columns = 2,
}: {
  rows?: number;
  /** 2 is the form grid; 1 is a single stacked column. */
  columns?: 1 | 2;
}) {
  return (
    <div
      aria-hidden="true"
      data-skeleton="true"
      className={columns === 2 ? 'grid gap-4 sm:grid-cols-2' : 'flex flex-col gap-4'}
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          {/* The label row is `min-h-5` on every shared input; the bar sits inside it so the
              placeholder is the same height as the label it stands in for. */}
          <div className="flex min-h-5 items-center">
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}
